/**
 * @scope console
 *
 * SplitLayout — N-column grid with vertical dividers, the canonical
 * console layout primitive.
 *
 * Replaces hand-rolled `<div className="grid grid-cols-[X_Y_Z] divide-x
 * divide-line-base">` blocks scattered across the YaaS console pages
 * (Policies' 20/30/50, Dashboard's 3fr/7fr, etc.). One component, one
 * source of truth for column rhythm + divider styling + responsive
 * stacking.
 */

import type React from "react";

import { cn } from "../../utils/cn";

export interface SplitColumn {
    /**
     * Column size. Two ways to spell it:
     *   - `number`  → CSS `fr` units (proportional). `3` ≡ `"3fr"`.
     *                 Use when columns are sized by ratio:
     *                 `[3, 7]` → 30/70, `[2, 3, 5]` → 20/30/50.
     *   - `string`  → any valid grid-template-columns value:
     *                 percentages (`"20%"`), fixed pixels (`"240px"`),
     *                 content-sized (`"auto"`), or
     *                 ranges (`"minmax(200px, 1fr)"`).
     *
     * Mix freely — e.g. `[{ size: "240px" }, { size: 1 }]` for a
     * fixed sidebar + flex main content.
     */
    size: number | string;
    content: React.ReactNode;
    /**
     * Per-column wrapper class. `min-w-0` is always applied so
     * children can shrink inside the track; this layers on top
     * (e.g. `"flex flex-col pb-6"`).
     */
    className?: string;
}

export interface SplitLayoutProps {
    columns: SplitColumn[];
    /**
     * Breakpoint at which columns start splitting. Below this,
     * everything stacks vertically. Default `"md"`.
     */
    stackBelow?: "sm" | "md" | "lg";
    /**
     * Vertical 1px divider between columns (matches every existing
     * console page that uses this pattern). Default `true`.
     */
    divider?: boolean;
    /** Extra classes on the outer grid wrapper. */
    className?: string;
}

const STACK_CLASS: Record<
    NonNullable<SplitLayoutProps["stackBelow"]>,
    string
> = {
    // Tailwind needs the literal class strings present at build
    // time, hence the lookup map. The CSS variable `--cols` carries
    // the dynamic track template through to grid-template-columns.
    sm: "sm:grid-cols-[var(--cols)]",
    md: "md:grid-cols-[var(--cols)]",
    lg: "lg:grid-cols-[var(--cols)]"
};

export function SplitLayout({
    columns,
    stackBelow = "md",
    divider = true,
    className
}: SplitLayoutProps): React.ReactElement {
    const template = columns
        .map((c) => (typeof c.size === "number" ? `${c.size}fr` : c.size))
        .join(" ");

    return (
        <div
            className={cn(
                "grid grid-cols-1",
                STACK_CLASS[stackBelow],
                divider && "divide-x divide-line-base",
                className
            )}
            style={{ "--cols": template } as React.CSSProperties}
        >
            {columns.map((col, i) => (
                <div key={i} className={cn("min-w-0", col.className)}>
                    {col.content}
                </div>
            ))}
        </div>
    );
}
